import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tag, Trash2, Plus } from "lucide-react";

const COLORS = ["#10B981", "#059669", "#3B82F6", "#6366F1", "#8B5CF6", "#EC4899", "#F43F5E", "#F59E0B", "#EAB308", "#64748B"];

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "expense", color: COLORS[6] });

  const load = useCallback(async () => {
    const r = await api.get("/categories");
    setCats(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post("/categories", form);
      toast.success("Kategori eklendi");
      setForm({ name: "", kind: "expense", color: COLORS[6] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Kategori silinsin mi? Bu kategoriye bağlı işlemler kategorisiz kalır.")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Silindi"); load(); } catch { toast.error("Hata"); }
  };

  const income = cats.filter((c) => c.kind === "income");
  const expense = cats.filter((c) => c.kind === "expense");

  const List = ({ title, data, color }) => (
    <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
        <span className={`text-xs px-2 py-1 rounded-full ${color}`}>{data.length} adet</span>
      </div>
      {data.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center">Kategori yok.</div>
      ) : (
        <div className="space-y-1.5">
          {data.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`cat-${c.id}`}>
              <span className="w-3 h-3 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 text-sm font-medium">{c.name}</span>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => del(c.id)}
                data-testid={`cat-delete-${c.id}`}
                className="h-7 w-7 text-slate-400 hover:text-rose-400"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">Yönetim</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Kategoriler</h1>
        <p className="text-sm text-slate-400 mt-1">Gelir ve gider kategorilerinizi özelleştirin.</p>
      </div>

      <div className="bg-[#131924] border border-white/5 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-4 h-4 text-indigo-400" />
          <h2 className="font-display text-lg font-bold tracking-tight">Yeni Kategori Ekle</h2>
        </div>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400">Ad</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name-input" className="mt-2 bg-[#0F1522] border-white/10" placeholder="Örn: Pazarlama" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Tür</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="cat-kind-select" className="mt-2 bg-[#0F1522] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#131924] border-white/10 text-slate-100">
                <SelectItem value="income">Gelir</SelectItem>
                <SelectItem value="expense">Gider</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400">Renk</Label>
            <div className="mt-2 flex items-center gap-1.5">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  data-testid={`cat-color-${c.replace("#", "")}`}
                  className={`w-7 h-7 rounded-full transition-all ${form.color === c ? "ring-2 ring-white/60 scale-110" : ""}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          <Button type="submit" data-testid="cat-save-button" className="bg-indigo-500 hover:bg-indigo-600 h-10">
            <Plus className="w-4 h-4 mr-1.5" /> Ekle
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <List title="Gelir Kategorileri" data={income} color="bg-emerald-500/10 text-emerald-300" />
        <List title="Gider Kategorileri" data={expense} color="bg-rose-500/10 text-rose-300" />
      </div>
    </div>
  );
}
